/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, Link, RangeSlider, TextStyle } from '@shopify/polaris';
import { ExternalSmallMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { Field, change, getFormValues } from 'redux-form';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';

import {
  TEXT_TRANSFORM,
  TEXT_ALIGNMENT,
  LAYOUT_DISPLAY,
  DESIGN_STYLE_SETTINGS,
  BUNDLE_STYLE
} from 'src/constants/recommendation';
import { URL } from 'src/constants/links';

import {
  isMinValue,
  minValue,
  maxValue,
  isRequired,
  nonXSS
} from 'src/helpers/form-validation-helper';
import { normalizeNumberRangeSlider } from 'src/helpers/form-validation-helper';
import { getShop, buildOptionList } from 'src/helpers';
import { routes } from 'src/routes';

import type { RecommendationWidgetType, WidgetDesignSettingsType } from 'src/models/recommendation';

type Props = {
  formValues: RecommendationWidgetType,
  recommendationType: string,
  widgetDesignSettings: WidgetDesignSettingsType,
  widgetDesignShareableSettings: WidgetDesignSettingsType,
  fontList: Array<Object>,
  fontFamily: Array<Object>,
  fontListSuccess: boolean,
  updateField: (field, value) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  textTransform: Array<Object>,
  titleAlignment: Array<Object>,
  fontStyle: Array<Object>,
  designStyles: Array<Object>,
  checkDesignStyle: string,
  layoutDisplay: Array<Object>,
  maxNumberOfProductInWidget: number,
  titleFontStyles: Array<Object>,
  customizationTitleStyle: Object
};

const isRequiredValue = isRequired();
const isMaxValueCarousel = maxValue(30);
const isMaxValueGrid = maxValue(15);
const isMaxValueBundle = maxValue(5);
const isMaxValuePerRow = maxValue(6);
const isMinValueNumber = isMinValue(1);
const isMinValueSize = minValue(2);
const nonXSSValue = nonXSS();

class SettingsVisualEditor extends Component<ComposedProps, State> {
  state = {
    textTransform: [],
    titleAlignment: [],
    fontStyle: [],
    designStyles: [],
    bundleStyles: [],
    checkDesignStyle: '',
    layoutDisplay: [],
    maxNumberOfProductInWidget: 30,
    titleFontStyles: [],
    customizationTitleStyle: {}
  };

  componentDidMount() {
    const { i18n, widget, formValues } = this.props;
    const textTransform = buildOptionList(TEXT_TRANSFORM, i18n);
    const titleAlignment = buildOptionList(TEXT_ALIGNMENT, i18n);
    const designStyles = buildOptionList(DESIGN_STYLE_SETTINGS, i18n);
    const bundleStyles = buildOptionList(BUNDLE_STYLE, i18n);
    let layoutDisplay = buildOptionList(LAYOUT_DISPLAY, i18n);
    let layoutDisplayWithoutBundle = layoutDisplay.filter(e => e.value !== 'bundle');
    const { recommendationType, pageType } = widget;

    const checkDesignStyle = _.get(formValues, 'templateType', '');
    // Get currentLayoutDisplay to get max number value of products in widget
    const currentLayoutDisplay = LAYOUT_DISPLAY.find(
      e => e.value === _.get(formValues, 'layoutDisplay', 'carousel')
    );

    this.setState({
      textTransform,
      titleAlignment,
      designStyles,
      bundleStyles,
      layoutDisplay:
        ['frequently-bought-together', 'related-items'].includes(recommendationType) &&
        pageType === 'product-page'
          ? layoutDisplay
          : layoutDisplayWithoutBundle,
      checkDesignStyle,
      maxNumberOfProductInWidget:
        recommendationType === 'hand-picked-products'
          ? 15
          : currentLayoutDisplay.maxNumberProductInWidget
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n, formValues, recommendationType, fontList } = nextProps;
    const { i18n: prevI18n, formValues: prevFormValues, touch } = this.props;
    const { titleFontStyles } = this.state;
    if (i18n.locale !== prevI18n.locale) {
      const titleAlignment = buildOptionList(TEXT_ALIGNMENT, i18n);
      const textTransform = buildOptionList(TEXT_TRANSFORM, nextProps.i18n);
      this.setState({
        titleAlignment,
        textTransform
      });
    }
    // Update design style to show widget title
    if (_.get(formValues, 'templateType', '') !== _.get(prevFormValues, 'templateType', '')) {
      this.setState({
        checkDesignStyle: _.get(formValues, 'templateType', '')
      });
    }

    // Get max number value of products in widget
    if (_.get(formValues, 'layoutDisplay', '') !== _.get(prevFormValues, 'layoutDisplay', '')) {
      const layoutDisplay = LAYOUT_DISPLAY.find(
        e => e.value === String(_.get(formValues, 'layoutDisplay', 'carousel'))
      );
      this.setState({
        maxNumberOfProductInWidget:
          recommendationType === 'hand-picked-products'
            ? 15
            : layoutDisplay.maxNumberProductInWidget
      });
      touch('numberOfRecommendProduct');
    }
    // Set font style default when first load
    if (
      _.get(this.props, 'fontList.length', 0) !== _.get(nextProps, 'fontList.length', 0) ||
      (fontList.length > 0 && titleFontStyles.length === 0)
    ) {
      const titleFont = _.get(formValues, 'titleFont', 'Poppins');
      this.updateFontStyle('titleFontStyles', titleFont, true, fontList);
    }
  }
  render() {
    const {
      textTransform,
      titleAlignment,
      titleFontStyles,
      designStyles,
      bundleStyles,
      layoutDisplay,
      checkDesignStyle,
      maxNumberOfProductInWidget
    } = this.state;
    const {
      i18n,
      updateField,
      formValues,
      fontFamily,
      widget,
      widgetDesignShareableSettings,
      currentSection,
      host
    } = this.props;
    const { recommendationType } = widget;
    const titleFontSize = _.get(formValues, 'titleFontSize', 1);
    const currentLayout = String(_.get(formValues, 'layoutDisplay', 'carousel'));
    const titleTextColor = _.get(formValues, 'titleTextColor', '#EA3838');
    const shop = getShop();

    // Set validate max value product for recommendation widget
    let isMaxValue = isMaxValueCarousel;
    switch (currentLayout) {
      case 'carousel':
        isMaxValue =
          recommendationType === 'hand-picked-products' ? isMaxValueGrid : isMaxValueCarousel;
        break;
      case 'grid':
        isMaxValue = isMaxValueGrid;
        break;
      default:
        isMaxValue = isMaxValueBundle;
    }

    const title = currentSection === 0 ? 'cardSection02_Title' : 'cardSection03_Title';

    return (
      <div className="visual-editor-settings">
        <form>
          <div className="card-section title-bold-normal word-break card-title-uppercase">
            <TextStyle variation="subdued">
              {i18n.translate(`AdminHTML.Recommendation.VisualEditor.${title}`)}
            </TextStyle>
          </div>
          {currentSection === 0 ? (
            <div className="card-section">
              {/* Widget layout settings */}
              <div className="widget-layout-settings">
                <Field
                  name="layoutDisplay"
                  component={RenderChoice}
                  label={i18n.translate(
                    'AdminHTML.Recommendation.VisualEditor.cardSection02_Label02'
                  )}
                  choices={layoutDisplay}
                  onChange={value => this.onChangeLayoutDisplay(value)}
                />
                <Field
                  name="numberOfRecommendProduct"
                  component={RenderTextField}
                  label={i18n.translate(
                    'AdminHTML.Recommendation.VisualEditor.cardSection02_Label03'
                  )}
                  type="number"
                  tooltip={i18n.translate('AdminHTML.Recommendation.VisualEditor.tooltipContent')}
                  min={1}
                  max={maxNumberOfProductInWidget}
                  validate={[isRequiredValue, isMaxValue, isMinValueNumber]}
                  normalize={(value, prevValue) => normalizeNumberRangeSlider(value, prevValue)}
                />
                {currentLayout === 'bundle' ? (
                  <Field
                    name="bundleStyle"
                    component={RenderSelect}
                    label={i18n.translate(
                      'AdminHTML.Recommendation.VisualEditor.cardSection02_Label05'
                    )}
                    options={bundleStyles}
                  />
                ) : (
                  <Field
                    name="numberOfProductPerRow"
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.Recommendation.VisualEditor.cardSection02_Label04'
                    )}
                    type="number"
                    min={1}
                    max={6}
                    validate={[isRequiredValue, isMaxValuePerRow, isMinValueNumber]}
                    normalize={(value, prevValue) => normalizeNumberRangeSlider(value, prevValue)}
                  />
                )}

                <div className="recommendation-note">
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.Recommendation.VisualEditor.layout_note1')}
                  </TextStyle>
                  <TextStyle variation="subdued">
                    {i18n.translate('AdminHTML.Recommendation.VisualEditor.layout_note2', {
                      t1: (
                        <Link
                          external
                          url={`${routes.themeSetup.view()}?shop=${shop}&host=${host}`}
                          onClick={this.onThemeSetupClick}>
                          {i18n.translate('AdminHTML.Recommendation.VisualEditor.layout_note2_t1')}
                          <span className="external-link">
                            <Button
                              icon={ExternalSmallMinor}
                              plain
                              onClick={this.onThemeSetupClick}
                            />
                          </span>
                        </Link>
                      )
                    })}
                  </TextStyle>
                  <p>
                    <TextStyle variation="subdued">
                      <Link
                        external
                        url={URL['CustomizingProductItemSection']}
                        onClick={this.onLearnMoreClick}>
                        {i18n.translate('AdminHTML.Recommendation.VisualEditor.layout_note3')}
                        <span className="external-link">
                          <Button icon={ExternalSmallMinor} plain onClick={this.onLearnMoreClick} />
                        </span>
                      </Link>
                    </TextStyle>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-section">
              {/* Widget title settings */}
              <div className="widget-title-settings">
                <Field
                  name="widgetName"
                  component={RenderTextField}
                  label={i18n.translate(
                    'AdminHTML.Recommendation.VisualEditor.cardSection02_Label01'
                  )}
                  validate={[isRequiredValue, nonXSSValue]}
                />
                {widgetDesignShareableSettings && (
                  <Field
                    name="templateType"
                    label={i18n.translate(
                      'AdminHTML.Recommendation.VisualEditor.designStyleSettings_Title'
                    )}
                    component={RenderSelect}
                    options={designStyles}
                    onChange={value => this.onChangeTemplateType(value)}
                  />
                )}
                {widgetDesignShareableSettings && checkDesignStyle === 'customization' && (
                  <div className="default-settings-widget" style={{ paddingTop: '16px' }}>
                    {i18n.translate(
                      'AdminHTML.Recommendation.VisualEditor.notifyCustomizationSettings'
                    )}
                  </div>
                )}
                {(!checkDesignStyle || checkDesignStyle === 'customization') && (
                  <>
                    <Field
                      name="titleAlignment"
                      label={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.cardSection03_Label01'
                      )}
                      component={RenderSelect}
                      options={titleAlignment}
                    />
                    <RenderColorPickerHexPopover
                      colorValue={titleTextColor}
                      fieldName="titleTextColor"
                      fieldLabel={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.cardSection03_Label02'
                      )}
                      updateField={updateField}
                    />

                    <Field
                      name="titleFont"
                      label={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.cardSection03_Label03'
                      )}
                      component={RenderSelect}
                      onChange={value => this.updateFontStyle('titleFontStyles', value)}
                      options={fontFamily}
                    />
                    <Field
                      name="titleTextTransform"
                      label={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.cardSection03_Label04'
                      )}
                      component={RenderSelect}
                      options={textTransform}
                    />
                    <Field
                      name="titleFontStyle"
                      label={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.cardSection03_Label05'
                      )}
                      component={RenderSelect}
                      options={titleFontStyles}
                    />
                    <div className="range-slider-input">
                      <Field
                        name="titleFontSize"
                        label={i18n.translate(
                          'AdminHTML.Recommendation.VisualEditor.cardSection03_Label06'
                        )}
                        component={RenderTextField}
                        onBlur={(event, value) =>
                          this.onBlurTextField(event, value, 'titleFontSize')
                        }
                        normalize={(value, prevValue) =>
                          normalizeNumberRangeSlider(value, prevValue, 50)
                        }
                        validate={[isMinValueSize]}
                        anotherProps={{
                          connectedLeft: (
                            <RangeSlider
                              value={titleFontSize}
                              onChange={value => updateField('titleFontSize', value)}
                              max={50}
                              min={2}
                            />
                          )
                        }}
                      />
                    </div>
                    <Field
                      name="isDefault"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.Recommendation.VisualEditor.checkboxContent01'
                      )}
                    />
                    <div className="default-settings-widget">
                      {i18n.translate(
                        `AdminHTML.Recommendation.VisualEditor.${
                          widgetDesignShareableSettings ? 'checkboxContent03' : 'checkboxContent02'
                        }`
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    );
  }
  // on blur textfield
  onBlurTextField = (event, value, name) => {
    const { updateField } = this.props;
    if (!value) {
      event.preventDefault();
      updateField(name, value);
    }
  };

  // Handle change font
  updateFontStyle = (field, selected, firstLoad = false, fontListNext) => {
    if (!selected) return;

    const { fontList, updateField } = this.props;

    const list = fontList.length === 0 ? fontListNext : fontList;
    const font = _.find(list, font => {
      return font.family === selected;
    });
    if (!font) return;
    const variants = _.get(font, 'variants', []);

    const fontStyles = variants.map(variant => ({ label: variant, value: variant }));
    this.setState({ [field]: fontStyles });

    // Auto select first item if not first loading
    if (!firstLoad) {
      updateField('titleFontStyle', fontStyles[0].value);
    }
  };

  // update isDefault (Save Widget title settings above as Default.) when change templateType
  onChangeTemplateType = value => {
    const { updateField, widgetDesignShareableSettings, formValues } = this.props;
    updateField('isDefault', value === 'default');
    if (value === 'default') {
      // backup customization title settings before set default title settings
      const {
        titleTextColor = '#3D4246',
        titleFont = 'Poppins',
        titleFontStyle = '100',
        titleFontSize = 14,
        titleTextTransform = 'capitalize',
        titleAlignment = 'left'
      } = formValues;
      this.setState({
        customizationTitleStyle: {
          titleTextColor,
          titleFont,
          titleFontStyle,
          titleFontSize,
          titleTextTransform,
          titleAlignment
        }
      });
      const defaultTitleSetting = {
        titleTextColor: widgetDesignShareableSettings.titleTextColor || '#3D4246',
        titleFont: widgetDesignShareableSettings.titleFont || 'Poppins',
        titleFontStyle: widgetDesignShareableSettings.titleFontStyle || '100',
        titleFontSize: widgetDesignShareableSettings.titleFontSize || 14,
        titleTextTransform: widgetDesignShareableSettings.titleTextTransform || 'capitalize',
        titleAlignment: widgetDesignShareableSettings.titleAlignment || 'left'
      };
      for (const key of Object.keys(defaultTitleSetting)) {
        updateField(key, defaultTitleSetting[key]);
      }
    } else {
      // revert customization title style
      if (!_.isEmpty(this.state.customizationTitleStyle)) {
        for (const key of Object.keys(this.state.customizationTitleStyle)) {
          updateField(key, this.state.customizationTitleStyle[key]);
        }
      }
    }
  };

  // update default value for numberOfRecommendProduct, numberOfProductPerRow, bundleStyle when change layout display
  onChangeLayoutDisplay = value => {
    const { bundleStyles } = this.state;
    const { updateField, widgetDesignSettings } = this.props;
    const defaultMaxNumberProductInWidget =
      LAYOUT_DISPLAY.filter(layoutDisplay => layoutDisplay.value === String(value))[0]
        .defaultMaxNumberProductInWidget || 4;
    if (widgetDesignSettings.layoutDisplay !== String(value)) {
      updateField('numberOfRecommendProduct', defaultMaxNumberProductInWidget);
      updateField('numberOfProductPerRow', 4);
    } else {
      updateField('numberOfRecommendProduct', widgetDesignSettings.numberOfRecommendProduct);
      updateField('numberOfProductPerRow', widgetDesignSettings.numberOfProductPerRow);
    }
    if (!widgetDesignSettings.bundleStyle)
      updateField('bundleStyle', bundleStyles[0].value || 'style1');
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (field, value) => dispatch(change(`VisualEditorForm`, field, value))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('VisualEditorForm')(state);
  const widgetDesignShareableSettings = _.get(
    state.recommendation,
    'widgetDesignShareableSettings',
    {}
  );
  const widgetDesignSettings = _.get(props.widget, 'widgetDesignSettings', {});
  return {
    formValues,
    widgetDesignSettings,
    widgetDesignShareableSettings,
    fontList: _.get(state, 'font.fontList', []),
    fontFamily: _.get(state, 'font.fontFamily', []),
    fontListSuccess: _.get(state, 'font.fontListSuccess', false),
    host: _.get(state.oauth, 'host', '')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SettingsVisualEditor));
